import { useEffect, useState } from 'react';
import algoliasearchHelper from 'algoliasearch-helper';

import { getUserLocation } from '@utils/location';
import { initializeSearchClient, getLocationRestrictionFilter } from '@utils/algoliaUtils.cjs';
import { processCourseSearchResult } from '@utils/searchResults';

const INDEX_NAME = 'product_subject_and_skills_recommendations';

/**
 * Fetches course recommendations from Algolia based on the list of subjects and skills passed.
 * Algolia Index: 'product_subject_and_skills_recommendations'
 * @param {object} params An object containing list skills, subjects and
 * courseRunKeysToExclude (The courses that we want to exclude in recommendations).
 * @param {boolean} paramsLoading A flag denoting if the params are loading or not. (optional).
 * @returns {{isAlgoliaRecommendationsLoading: boolean, algoliaRecommendations: *[]}} a list of
 * recommended courses and a bool denoting if recommendations are loading
 */
function useAlgoliaRecommendations(params, paramsLoading = false) {
  const {
    courseRunKeysToExclude = [], skills = [], subjects = [], languages = [],
  } = params;
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const userCountry = getUserLocation();

  useEffect(() => {
    if (paramsLoading || !params?.skills || !params?.subjects) { return; }

    const getSearchFiltersQueryString = () => {
      const queryFilters = [];
      if (userCountry) { queryFilters.push(getLocationRestrictionFilter(userCountry)); }

      let filters = '';
      if (subjects.length > 0 || skills.length > 0) {
        subjects.forEach((subject) => {
          filters += `subject:"${subject}" OR `;
        });
        skills.forEach((skill) => {
          filters += `skills.skill:"${skill}" OR `;
        });
        filters = filters.slice(0, -4);
        queryFilters.push(`(${filters})`);
      }

      let languageFilters = '';
      if (languages.length > 0) {
        languages.forEach((language) => {
          languageFilters += `language:"${language}" OR `;
        });
        languageFilters = languageFilters.slice(0, -4);
        queryFilters.push(`(${languageFilters})`);
      }
      return queryFilters.join(' AND ');
    };

    const algoliaSearchParams = {
      facets: ['product', 'availability', 'active_run_key'],
      filters: getSearchFiltersQueryString(),
      sumOrFiltersScores: true,
    };

    const searchClient = initializeSearchClient();
    const searchHelper = algoliasearchHelper(
      searchClient,
      INDEX_NAME,
      algoliaSearchParams,
    );

    searchHelper.addFacetRefinement('product', 'Course');
    searchHelper.addFacetRefinement('availability', 'Available Now');
    courseRunKeysToExclude.forEach((courseRunKey) => {
      searchHelper.addFacetRefinement('active_run_key', `-${courseRunKey}`);
    });

    const searchIndex = () => {
      setIsLoading(true);
      searchHelper.search();
    };

    searchIndex();

    searchHelper.on('result', ({ results }) => {
      setRecommendations(results.hits.map(
        (hit, index) => (
          processCourseSearchResult(hit, index)
        ),
      ));
      setIsLoading(false);
    });

    searchHelper.on('error', () => setIsLoading(false));
  }, [paramsLoading]);

  return {
    algoliaRecommendations: recommendations,
    isAlgoliaRecommendationsLoading: isLoading,
  };
}

export default useAlgoliaRecommendations;
